import { DoneButton } from 'components/blue-button'
import EditActionRow from 'components/lesson-builder/components/edit-action-row'
import React, { useState, useEffect } from 'react'
import 'css/components/lesson-builder/question-types.css'
import { Question } from 'types'
import type { ShortAnswerQuestion } from 'types'
import AddImageOverlay from 'components/lesson-builder/components/add-image-overlay'
import styles from 'css/components/lesson-builder/short-answer.module.css'
// Type guard for short answer questions
const isShortAnswerQuestion = (question: Question): question is ShortAnswerQuestion => {
  return question.type === 'short-answer'
}

interface ShortAnswerAnsweringProps {
  question: Question
  answer?: string
  onAnswerChange?: (answer: string) => void
  maxLength?: number
}

export const ShortAnswerAnswering = ({
  question,
  answer = '',
  onAnswerChange,
  maxLength = 500
}: ShortAnswerAnsweringProps) => {
  const [answerText, setAnswerText] = useState(answer)

  // Type guard to ensure this is a short answer question
  if (!isShortAnswerQuestion(question)) {
    return <div>Invalid question type for short answer</div>
  }

  return (
    <div className="question-type short-answer">
      {question.imageUrl && <img className="question-image" src={question.imageUrl} alt="short answer" />}
      <p className="question-text">
         {Array.isArray(question.question) ? question.question.join(' ') : question.question}</p>
      <textarea
        className={styles.answerInput}
        value={answerText}
        onChange={(e) => {
          const newVal = e.target.value
          setAnswerText(newVal)
          if (onAnswerChange) {
            onAnswerChange(newVal)
          }
        }}
        maxLength={maxLength}
        placeholder="Type your answer here..."
        rows={4}
      />
      <div className={styles.characterCount}>
        {answerText.length}/{maxLength}
      </div>
      {/* Done button is controlled by parent (student-live) */}
    </div>
  )
} 

/** 
 * Short Answer View Component - displays the question and answer
 * @param {Object} props
 * @param {string} props.question
 * @param {string} props.answer
 */
export const ShortAnswerViewer = ({
  question,
  onRemoveImage,
}: { question: Question, onRemoveImage?: (optionIndex?: number) => void }) => {
  // Type guard to ensure this is a short answer question
  if (!isShortAnswerQuestion(question)) {
    return <div>Invalid question type for short answer viewer</div>
  }

  return (
    <div className="question-type short-answer">
      {question.imageUrl && (
        <div className="image-remove-wrapper">
          <img className="question-image" src={question.imageUrl} alt="short answer" />
          {onRemoveImage && <button className="remove-image-btn" onClick={() => onRemoveImage()} title="Remove image">✕</button>}
        </div>
      )}
      <p className="question-text">
        {Array.isArray(question.question) ? question.question.join(' ') : question.question}
      </p>
      <div className="question-answer">
        {Array.isArray(question.answer) ? question.answer.join(', ') : question.answer}
      </div>
    </div>
  )
} 

export const ShortAnswerEditor = ({ question, onEdit, setIsEditing, setShowFeedback }: { question: Question, onEdit: (payload: Partial<Question>) => void, setIsEditing: (isEditing: boolean) => void, setShowFeedback?: (showFeedback: boolean) => void }) => {
  const [questionText, setQuestionText] = useState('')
  const [answerText, setAnswerText] = useState('')
  const [showImageOverlay, setShowImageOverlay] = useState(false)

  // Update state with actual question data if this is a valid short answer question
  useEffect(() => {
    if (isShortAnswerQuestion(question)) {
      setQuestionText(question.question)
      setAnswerText(question.answer)
    }
  }, [question])

  // Type guard to ensure this is a short answer question
  if (!isShortAnswerQuestion(question)) {
    return <div>Invalid question type for short answer editor</div>
  }

  const handleDone = () => {
    onEdit({question: questionText, answer: answerText})
    setIsEditing(false)
  }

  const openQuestionImageOverlay = () => {
    setShowImageOverlay(true)
  }

  const handleUploadImage = (url: string) => {
    onEdit({ imageUrl: url })
    // keep overlay open; user will close via Done
  }

  return (
    <div className="question-type short-answer">
      {question.imageUrl && <img className="question-image" src={question.imageUrl} alt="short answer" />}
      Question:
      <textarea
        className="editor-input"  
        rows={2}
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
      ></textarea>
      {setShowFeedback && (
        <EditActionRow setShowFeedback={setShowFeedback} onEdit={onEdit} onAddImage={openQuestionImageOverlay} />
      )}
      Answer:
      <textarea
        className="editor-input"
        rows={4}
        value={answerText}
        onChange={(e) => setAnswerText(e.target.value)}
      ></textarea>
      {showImageOverlay && (
        <AddImageOverlay
          setShowAddImageOverlay={setShowImageOverlay}
          onUpload={handleUploadImage}
          title={'Add Question Image'}
          note={'Note: Only one image can be inserted before this question.'}
        />
      )}
      <DoneButton onClick={handleDone} />
    </div>
  )
}
